<script setup>
// ContactDetailHero.vue — hero compacto e sticky para a tela de detalhe
// do contato (rota /app/accounts/:accountId/contacts/:contactId).
//
// Layout (LTR):
//   [Avatar 64px] [Nome + email/phone + tags inline]   [Mensagem CTA] [Bloquear] [Mais]
//   ----------------------------------------------------------------------------
//   [ID #123] [Criado em X] [Última atividade Y] [N conversas]
//
// Sticky top no scroll do main. Sem max-w (full width).
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import { OnClickOutside } from '@vueuse/components';
import { dynamicTime } from 'shared/helpers/timeHelper';
import countries from 'shared/constants/countries';

const props = defineProps({
  contact: { type: Object, default: () => ({}) },
  conversationsCount: { type: Number, default: 0 },
  isUpdating: { type: Boolean, default: false },
});
const emit = defineEmits([
  'avatarUpload',
  'avatarDelete',
  'toggleBlock',
  'merge',
  'delete',
]);
const { accountScopedRoute } = useAccount();
const backRoute = computed(() =>
  accountScopedRoute('contacts_dashboard_index')
);

const { t } = useI18n();

const isMoreMenuOpen = ref(false);

const avatarUrlOverride = ref('');

const isBlocked = computed(() => Boolean(props.contact?.blocked));

const contactId = computed(() => props.contact?.id ?? '');
const contactName = computed(() => props.contact?.name ?? '');

const avatarSrc = computed(
  () => avatarUrlOverride.value || props.contact?.thumbnail || ''
);

const subtitleParts = computed(() => {
  const parts = [];
  if (props.contact?.email) parts.push(props.contact.email);
  if (props.contact?.phoneNumber) parts.push(props.contact.phoneNumber);
  return parts;
});

const createdLabel = computed(() => {
  if (!props.contact?.createdAt) return '';
  return t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', {
    date: dynamicTime(props.contact.createdAt),
  });
});

const lastActivityLabel = computed(() => {
  if (!props.contact?.lastActivityAt) return '';
  return t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
    date: dynamicTime(props.contact.lastActivityAt),
  });
});

const conversationsLabel = computed(() =>
  t('CONTACT_VIEW.HERO.CONVERSATIONS_COUNT', {
    count: props.conversationsCount,
  })
);

const countryDetails = computed(() => {
  const attrs = props.contact?.additionalAttributes || {};
  const { country, countryCode } = attrs;
  if (!country && !countryCode) return null;
  const match = countries.find(
    c => c.id === countryCode || c.code === country || c.id === country
  );
  if (!match) return null;
  return { countryCode: match.id, name: match.name };
});

const { isAdmin } = useAdmin();

// Mesclar exige admin no backend (ContactMergesController).
const moreMenuItems = computed(() => [
  ...(isAdmin.value
    ? [
        {
          label: t('CONTACT_VIEW.HERO.MORE.MERGE'),
          value: 'merge',
          action: 'merge',
          icon: 'i-lucide-shuffle',
        },
      ]
    : []),
  {
    label: t('CONTACT_VIEW.HERO.MORE.DELETE'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash-2',
  },
]);

const handleMoreAction = ({ action }) => {
  isMoreMenuOpen.value = false;
  if (action === 'merge') emit('merge');
  if (action === 'delete') emit('delete');
};

const handleAvatarUpload = payload => {
  avatarUrlOverride.value = payload.url || '';
  emit('avatarUpload', payload);
};

const handleAvatarDelete = () => {
  avatarUrlOverride.value = '';
  emit('avatarDelete');
};

const toggleBlock = () => emit('toggleBlock', isBlocked.value);
</script>

<template>
  <section class="sticky top-0 z-20 bg-n-background border-b border-n-weak">
    <div class="flex flex-col gap-3 px-6 py-4">
      <!-- Botao Voltar pra lista de Contatos. Preserva o ?page=N do
           historico do router (RouterLink usa router.back equivalente
           via :to). -->
      <RouterLink
        :to="backRoute"
        class="inline-flex items-center gap-1.5 -ml-1 px-2 py-1 rounded text-xs font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-1 hover:text-n-slate-12 w-fit"
      >
        <span class="i-lucide-arrow-left size-3.5" />
        {{ t('CONTACT_VIEW.NOT_FOUND.BACK') }}
      </RouterLink>
      <div class="flex items-start justify-between gap-4">
        <!-- Esquerda: avatar + identidade -->
        <div class="flex items-start gap-4 min-w-0 flex-1">
          <Avatar
            :src="avatarSrc"
            :name="contactName"
            :size="64"
            allow-upload
            rounded-full
            @upload="handleAvatarUpload"
            @delete="handleAvatarDelete"
          />
          <div class="flex flex-col gap-1.5 min-w-0">
            <h1
              class="text-xl font-medium text-n-slate-12 leading-tight truncate"
            >
              {{ contactName || t('CONTACTS_LAYOUT.TABLE.UNNAMED') }}
            </h1>
            <div
              v-if="subtitleParts.length"
              class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-n-slate-11"
            >
              <template v-for="(part, idx) in subtitleParts" :key="part">
                <span class="truncate max-w-xs">{{ part }}</span>
                <span
                  v-if="idx < subtitleParts.length - 1"
                  class="w-px h-3 bg-n-strong"
                />
              </template>
            </div>
            <div class="mt-1">
              <ContactLabels :contact-id="contactId" />
            </div>
          </div>
        </div>

        <!-- Direita: acoes -->
        <div class="flex items-center gap-2 shrink-0">
          <VoiceCallButton
            :phone="contact?.phoneNumber"
            :contact-id="contact?.id"
            :contact-name="contact?.name"
            :contact-thumbnail="contact?.thumbnail"
            :label="$t('CONTACT_PANEL.CALL')"
            size="sm"
          />
          <Button
            :label="
              !isBlocked
                ? t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT')
                : t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT')
            "
            size="sm"
            variant="outline"
            color="slate"
            :is-loading="isUpdating"
            :disabled="isUpdating"
            @click="toggleBlock"
          />
          <ComposeConversation :contact-id="contactId">
            <template #trigger="{ toggle }">
              <Button
                :label="t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
                icon="i-lucide-message-square-plus"
                size="sm"
                color="teal"
                @click="toggle"
              />
            </template>
          </ComposeConversation>

          <!-- More menu -->
          <OnClickOutside @trigger="isMoreMenuOpen = false">
            <div class="relative">
              <Button
                icon="i-lucide-more-horizontal"
                variant="ghost"
                color="slate"
                size="sm"
                class="!w-8 !h-8"
                :aria-label="t('CONTACT_VIEW.HERO.MORE.LABEL')"
                @click="isMoreMenuOpen = !isMoreMenuOpen"
              />
              <DropdownMenu
                v-if="isMoreMenuOpen"
                :menu-items="moreMenuItems"
                class="mt-1 ltr:right-0 rtl:left-0"
                @action="handleMoreAction"
              />
            </div>
          </OnClickOutside>
        </div>
      </div>

      <!-- Chips de metadata -->
      <div
        class="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-n-slate-11"
      >
        <span
          v-if="contactId"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-n-alpha-2"
        >
          <span class="i-lucide-hash size-3" />
          {{ contactId }}
        </span>
        <span v-if="createdLabel" class="inline-flex items-center gap-1">
          <span class="i-lucide-calendar-plus size-3.5" />
          {{ createdLabel }}
        </span>
        <span v-if="lastActivityLabel" class="inline-flex items-center gap-1">
          <span class="i-lucide-activity size-3.5" />
          {{ lastActivityLabel }}
        </span>
        <span class="inline-flex items-center gap-1">
          <span class="i-lucide-message-circle size-3.5" />
          {{ conversationsLabel }}
        </span>
        <span v-if="countryDetails" class="inline-flex items-center gap-1.5">
          <Flag :country="countryDetails.countryCode" class="size-3.5" />
          {{ countryDetails.name }}
        </span>
        <span v-if="contact?.identifier" class="inline-flex items-center gap-1">
          <span class="i-lucide-fingerprint size-3.5" />
          {{ contact.identifier }}
        </span>
      </div>
    </div>
  </section>
</template>
